class Tab {
    constructor(selector = '.c-tab_header') {
        this.tablist = document.querySelector(selector);
        
        if (!this.tablist) return;
        
        this.tabs = Array.from(this.tablist.querySelectorAll('[role="tab"]'));
        this.panels = this.tabs.map(tab => 
            document.getElementById(tab.getAttribute('aria-controls'))
        );

        // Add class names for active states
        this.activeTabClass = 'active';
        this.activeContentClass = 'active';

        // Create ARIA live region for announcements
        this.liveRegion = document.createElement('div');
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.liveRegion.setAttribute('class', 'sr-only');
        this.tablist.parentNode.insertBefore(this.liveRegion, this.tablist.nextSibling);

        // Store the animation timelines
        this.dotAnimations = new Map();
        this.underline = this.tablist.querySelector('.c-tab_header_underline');

        // Constants for dot dimensions
        const firstTab = this.tabs[0];
        if (firstTab) {
            const computedStyle = getComputedStyle(firstTab);
            this.dotWidth = parseInt(computedStyle.getPropertyValue('--dot-size')) || 15;
            this.dotSpacing = parseInt(computedStyle.getPropertyValue('--padding-left')) || 30;
        } else {
            // Fallback values if no tabs exist
            this.dotWidth = 15;
            this.dotSpacing = 30;
        }
        this.animationDuration = 0.7;

        // Store original widths before setting fixed widths
        this.originalWidths = new Map();

        // Set initial button widths before initializing
        this.setInitialButtonWidths();

        // Store hover animation timeline
        this.hoverTimeline = null;

        // Bind the resize handler to this instance
        this.handleResize = this.handleResize.bind(this);

        // Store panel heights
        this.panelHeights = new Map();
        
        // Initialize panel heights and positions
        this.initPanelLayout();

        // Store both widths for each tab (with and without spacing)
        this.tabWidths = new Map();
        this.calculateTabWidths();

        // Add flag to track animation state
        this.isAnimating = false;

        // Initialize everything
        this.init();
    }

    init() {
        // Initialize animations first
        this.initDotAnimations();
        
        // Add event listeners
        this.tabs.forEach(tab => {
            tab.addEventListener('click', e => this.handleTabClick(e));
            tab.addEventListener('keydown', e => this.handleKeydown(e));
        });

        // Add resize listener
        window.addEventListener('resize', this.handleResize);

        // Set initial state
        if (this.tabs.length > 0) {
            this.setInitialState();
        }

        // Initialize hover animations last
        this.initHoverAnimation();
    }

    handleTabClick(event) {
        const targetTab = event.currentTarget;
        // Don't process click if tab is already active
        if (!targetTab.classList.contains(this.activeTabClass)) {
            this.switchToTab(targetTab);
        }
    }

    handleKeydown(event) {
        const targetTab = event.currentTarget;
        const currentIndex = this.tabs.indexOf(targetTab);
        
        let newTab = null;

        switch(event.key) {
            case 'ArrowLeft':
                newTab = this.tabs[currentIndex - 1] || this.tabs[this.tabs.length - 1];
                break;
            case 'ArrowRight':
                newTab = this.tabs[currentIndex + 1] || this.tabs[0];
                break;
            case 'Home':
                newTab = this.tabs[0];
                break;
            case 'End':
                newTab = this.tabs[this.tabs.length - 1];
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                this.switchToTab(targetTab);
                return;
            default:
                return;
        }

        if (newTab) {
            event.preventDefault();
            this.switchToTab(newTab);
        }
    }

    // Updated switchToTab method with improved timing
    switchToTab(targetTab) {
        // Set animating flag
        this.isAnimating = true;

        const getAccurateOffset = (element) => {
            const rect = element.getBoundingClientRect();
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            return rect.left + scrollLeft;
        };

        const previousTab = this.tabs.find(tab => tab.classList.contains('active'));
        const previousPanel = previousTab ? document.getElementById(previousTab.getAttribute('aria-controls')) : null;
        const newPanel = document.getElementById(targetTab.getAttribute('aria-controls'));

        // Announce tab change to screen readers
        const tabLabel = targetTab.querySelector('span').textContent;
        this.liveRegion.textContent = `Switched to ${tabLabel} tab`;

        // Update tab states first
        this.tabs.forEach(tab => {
            const isTargetTab = tab === targetTab;
            tab.setAttribute('aria-selected', isTargetTab);
            tab.tabIndex = isTargetTab ? 0 : -1;
            
            if (isTargetTab) {
                tab.classList.add(this.activeTabClass);
            } else {
                tab.classList.remove(this.activeTabClass);
            }

            const parentItem = tab.closest('.c-tab_header_item');
            if (parentItem) {
                if (isTargetTab) {
                    parentItem.classList.add(this.activeTabClass);
                } else {
                    parentItem.classList.remove(this.activeTabClass);
                }
            }
        });

        // Update panel states
        if (previousPanel) {
            previousPanel.setAttribute('hidden', '');
            previousPanel.classList.remove(this.activeContentClass);
            // Remove focusable elements from hidden panel
            previousPanel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])').forEach(el => {
                el.setAttribute('tabindex', '-1');
            });
        }
        if (newPanel) {
            newPanel.removeAttribute('hidden');
            newPanel.classList.add(this.activeContentClass);
            // Make focusable elements in new panel focusable
            newPanel.querySelectorAll('button, [href], input, select, textarea, [tabindex="-1"]').forEach(el => {
                el.removeAttribute('tabindex');
            });
        }

        // Handle tab dot animations for the previously active tab
        if (previousTab) {
            const previousTextElement = previousTab.querySelector('span');
            if (previousTextElement) {
                // Create a timeline for the deactivation animation sequence
                const deactivateTimeline = gsap.timeline();
                
                // First animate the padding
                deactivateTimeline.to(previousTextElement, {
                    paddingLeft: 0,
                    duration: 0.5,
                    ease: "power2.inOut"
                });
                
                // Then animate the dot with a 30% delay into the padding animation
                deactivateTimeline.to(previousTab, {
                    "--dot-y": "10px",
                    "--dot-opacity": 0,
                    duration: 0.5,
                    ease: "power2.out"
                }, "<"); // This starts 35% into the padding animation (0.5 * 70% = 0.35)
            }
        }

        // Update the underline animation to clear the flag when complete
        gsap.timeline({
            onComplete: () => {
                this.isAnimating = false;
            }
        })
        .to(this.underline, {
            x: getAccurateOffset(targetTab) - getAccurateOffset(this.tablist),
            width: this.tabWidths.get(targetTab).natural,
            duration: 0.3,
            ease: "power2.inOut"
        })
        .to(this.underline, {
            width: this.tabWidths.get(targetTab).full,
            duration: 0.3,
            ease: "power2.inOut"
        });

        // Play dot animation for new tab - with the specified timing
        const textElement = targetTab.querySelector('span');
        if (textElement) {
            // Create a timeline for activation animation sequence
            const activateTimeline = gsap.timeline();
            
            // First animate the padding
            activateTimeline.to(textElement, {
                paddingLeft: `${this.dotSpacing}px`,
                duration: 0.5,
                ease: "power2.inOut"
            });
            
            // Then animate the dot with a 30% delay into the padding animation
            activateTimeline.fromTo(targetTab, 
                {
                    "--dot-y": "10px", 
                    "--dot-opacity": 0
                },
                {
                    "--dot-y": "0px",
                    "--dot-opacity": 1,
                    duration: 0.5,
                    ease: "power2.out"
                },
                "-=30%" // This starts 35% into the padding animation (0.5 * 70% = 0.35)
            );
        }

        // Animate tab content transition
        if (previousPanel && newPanel) {
            const container = previousPanel.parentElement;
            const newHeight = this.panelHeights.get(newPanel);

            // Prepare new panel
            gsap.set(newPanel, {
                zIndex: 2,
                visibility: 'visible'
            });

            const contentTimeline = gsap.timeline({
                onComplete: () => {
                    // Reset previous panel
                    gsap.set(previousPanel, {
                        zIndex: 1,
                        visibility: 'hidden'
                    });
                    // Focus the tab button instead of panel content to prevent scrolling
                    targetTab.focus();
                }
            });

            contentTimeline
                // Animate container height
                .to(container, {
                    height: newHeight,
                    duration: 0.3,
                    ease: "power2.inOut"
                })
                // Animate out old panel
                .to(previousPanel, {
                    opacity: 0,
                    y: -20,
                    duration: 0.3,
                    ease: "power2.inOut"
                }, 0)
                // Animate in new panel
                .fromTo(newPanel, 
                    {
                        opacity: 0,
                        y: 20
                    },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        ease: "power2.out"
                    }, 
                    0.15
                );
        }

        targetTab.focus();
    }
    
    setInitialState() {
        // Set first tab as selected and others as not selected
        this.tabs.forEach((tab, index) => {
            const isFirst = index === 0;
            tab.setAttribute('aria-selected', isFirst);
            tab.tabIndex = isFirst ? 0 : -1;
            
            if (isFirst) {
                tab.classList.add(this.activeTabClass);
                // Get the timeline for the active tab
                const timeline = this.dotAnimations.get(tab);
                if (timeline) {
                    timeline.progress(1);
                }
            }
        });

        // Show first panel, hide others
        this.panels.forEach((panel, index) => {
            const isFirst = index === 0;
            panel.hidden = !isFirst;
            if (isFirst) {
                panel.classList.add(this.activeContentClass);
            }
        });

        // Set initial underline position with full width
        if (this.underline && this.tabs[0]) {
            gsap.set(this.underline, {
                x: this.tabs[0].offsetLeft,
                width: this.tabs[0].offsetWidth, // Full width with spacing
                opacity: 1
            });
        }
    }

    initHoverAnimation() {
        // Create hover animation for the parent items instead of the buttons
        this.tabs.forEach(tab => {
            const parentItem = tab.closest('.c-tab_header_item');
            if (parentItem) {
                parentItem.addEventListener('mouseenter', () => this.handleTabHover(tab));
                parentItem.addEventListener('mouseleave', () => this.handleTabHoverExit());
            }
        });
    }

    handleTabHover(tab) {
        // Skip hover animation if currently animating or tab is active
        if (this.isAnimating || tab.classList.contains('active')) {
            return;
        }

        if (this.hoverTimeline) {
            this.hoverTimeline.kill();
        }

        this.hoverTimeline = gsap.to(this.underline, {
            x: tab.offsetLeft,
            width: this.tabWidths.get(tab).natural,
            opacity: 1,
            duration: 0.4,
            ease: 'power2.out'
        });
    }

    handleTabHoverExit() {
        // Skip hover exit animation if currently animating
        if (this.isAnimating) {
            return;
        }

        const activeTab = this.tabs.find(tab => tab.classList.contains('active'));
        if (activeTab) {
            if (this.hoverTimeline) {
                this.hoverTimeline.kill();
            }

            this.hoverTimeline = gsap.to(this.underline, {
                x: activeTab.offsetLeft,
                width: this.tabWidths.get(activeTab).full,
                opacity: 1,
                duration: this.animationDuration,
                ease: 'power2.out'
            });
        }
    }

    setInitialButtonWidths() {
        this.tabs.forEach(tab => {
            // Store the natural width before modifying
            const naturalWidth = tab.offsetWidth;
            this.originalWidths.set(tab, naturalWidth);
            
            // All buttons get the extra spacing
            tab.style.width = `${naturalWidth + this.dotSpacing}px`;
            tab.style.textAlign = 'left';
        });
    }

    // The initDotAnimations method can be simplified since we're handling animation directly
    initDotAnimations() {
        // We still need this for the initial state
        if (!this.tabs.length) return;

        this.tabs.forEach(tab => {
            const textElement = tab.querySelector('span');
            if (!textElement) return;
            
            // Reset padding on non-active tabs
            if (!tab.classList.contains('active')) {
                gsap.set(textElement, { paddingLeft: 0 });
                gsap.set(tab, {
                    "--dot-y": "10px",
                    "--dot-opacity": 0
                });
            } else {
                // Set full padding on active tab
                gsap.set(textElement, { paddingLeft: `${this.dotSpacing}px` });
                gsap.set(tab, {
                    "--dot-y": "0px",
                    "--dot-opacity": 1
                });
            }
        });
    }

    waitForImages(panel, callback) {
        const images = panel.querySelectorAll('img');
        let loadedImages = 0;
        
        // If no images, set height immediately
        if (images.length === 0) {
            this.updatePanelHeight(panel, callback);
            return;
        }

        // Function to check if all images are loaded
        const checkAllLoaded = () => {
            loadedImages++;
            if (loadedImages === images.length) {
                this.updatePanelHeight(panel, callback);
            }
        };

        // Add load event listeners to each image
        images.forEach(img => {
            if (img.complete) {
                checkAllLoaded();
            } else {
                // Create a single event handler that removes itself after firing
                const handleLoad = () => {
                    img.removeEventListener('load', handleLoad);
                    img.removeEventListener('error', handleLoad);
                    checkAllLoaded();
                };
                
                img.addEventListener('load', handleLoad);
                img.addEventListener('error', handleLoad);
            }
        });
    }

    updatePanelHeight(panel, callback) {
        // Temporarily reset position to get true height
        const originalPosition = panel.style.position;
        const originalVisibility = panel.style.visibility;
        const originalOpacity = panel.style.opacity;
        const originalY = panel.style.y;
        
        gsap.set(panel, { 
            position: 'static', 
            visibility: 'visible', 
            opacity: 1,
            y: 0 
        });

        // Force a reflow to ensure accurate height calculation
        void panel.offsetHeight;

        // Get the height including margins
        const height = panel.offsetHeight + 
                      parseInt(getComputedStyle(panel).marginTop) + 
                      parseInt(getComputedStyle(panel).marginBottom);

        this.panelHeights.set(panel, height);

        // Restore original styles
        gsap.set(panel, { 
            position: originalPosition,
            visibility: originalVisibility,
            opacity: originalOpacity,
            y: originalY
        });

        if (callback) callback();
    }

    initPanelLayout() {
        if (!this.panels.length) return;

        // Get the container
        const container = this.panels[0].parentElement;
        
        // Set container to relative positioning
        gsap.set(container, { position: 'relative' });
        
        // Initialize each panel
        this.panels.forEach((panel, index) => {
            // Set initial styles
            gsap.set(panel, {
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                zIndex: index === 0 ? 2 : 1,
                visibility: index === 0 ? 'visible' : 'hidden',
                opacity: index === 0 ? 1 : 0,
                y: index === 0 ? 0 : 20
            });

            // Wait for all content to load before setting height
            this.waitForImages(panel, () => {
                // If this is the first panel, set initial container height
                if (index === 0) {
                    gsap.set(container, { height: this.panelHeights.get(panel) });
                }
            });
        });
    }

    calculateTabWidths() {
        this.tabs.forEach(tab => {
            // Temporarily remove any fixed width
            const originalWidth = tab.style.width;
            tab.style.width = '';

            // For active tabs, we need to account for existing padding
            let naturalWidth;
            if (tab.classList.contains('active')) {
                const textElement = tab.querySelector('span');
                // Temporarily remove padding to get true natural width
                const originalPadding = textElement.style.paddingLeft;
                textElement.style.paddingLeft = '0';
                naturalWidth = tab.offsetWidth;
                // Restore padding
                textElement.style.paddingLeft = originalPadding;
            } else {
                naturalWidth = tab.offsetWidth;
            }

            const fullWidth = naturalWidth + this.dotSpacing;
            
            // Store both widths
            this.tabWidths.set(tab, {
                natural: naturalWidth,
                full: fullWidth
            });

            // Restore or set new width
            tab.style.width = `${fullWidth}px`;
            tab.style.textAlign = 'left';
        });
    }

    // For the resize handler, we need to ensure we properly update
    handleResize() {
        if (this.resizeTimeout) {
            window.cancelAnimationFrame(this.resizeTimeout);
        }

        this.resizeTimeout = window.requestAnimationFrame(() => {
            // Get fresh CSS variable values first
            const firstTab = this.tabs[0];
            if (firstTab) {
                const computedStyle = getComputedStyle(firstTab);
                this.dotWidth = parseInt(computedStyle.getPropertyValue('--dot-size')) || 15;
                this.dotSpacing = parseInt(computedStyle.getPropertyValue('--padding-left')) || 30;
            }

            // Recalculate all tab widths with new values
            this.calculateTabWidths();

            // Update active tab
            const activeTab = this.tabs.find(tab => tab.classList.contains('active'));
            if (activeTab) {
                const textElement = activeTab.querySelector('span');
                if (textElement) {
                    gsap.set(textElement, { paddingLeft: `${this.dotSpacing}px` });
                }

                gsap.set(this.underline, {
                    x: activeTab.offsetLeft,
                    width: this.tabWidths.get(activeTab).full
                });
            }

            // Update panel heights
            this.panels.forEach(panel => {
                this.updatePanelHeight(panel, () => {
                    // If this is the active panel, update container height
                    if (activeTab && panel.id === activeTab.getAttribute('aria-controls')) {
                        gsap.set(panel.parentElement, { 
                            height: this.panelHeights.get(panel) 
                        });
                    }
                });
            });

            // Reset panels to absolute positioning
            this.panels.forEach(panel => {
                const isActivePanel = activeTab && panel.id === activeTab.getAttribute('aria-controls');
                gsap.set(panel, { 
                    position: 'absolute',
                    visibility: isActivePanel ? 'visible' : 'hidden',
                    opacity: isActivePanel ? 1 : 0,
                    y: isActivePanel ? 0 : 20,
                    zIndex: isActivePanel ? 2 : 1
                });
            });
        });
    }

    // Add cleanup method to remove event listener
    destroy() {
        // Remove window resize listener
        window.removeEventListener('resize', this.handleResize);
        
        // Cancel any pending resize timeout
        if (this.resizeTimeout) {
            window.cancelAnimationFrame(this.resizeTimeout);
        }

        // Kill hover animation
        if (this.hoverTimeline) {
            this.hoverTimeline.kill();
        }

        // Kill dot animations and clear the Map
        this.dotAnimations.forEach(timeline => {
            timeline.kill();
        });
        this.dotAnimations.clear();

        // Remove tab click and keydown listeners
        this.tabs.forEach(tab => {
            tab.removeEventListener('click', this.handleTabClick);
            tab.removeEventListener('keydown', this.handleKeydown);
            
            // Remove hover listeners from parent items
            const parentItem = tab.closest('.c-tab_header_item');
            if (parentItem) {
                parentItem.removeEventListener('mouseenter', () => this.handleTabHover(tab));
                parentItem.removeEventListener('mouseleave', () => this.handleTabHoverExit());
            }
        });

        // Remove ARIA live region
        if (this.liveRegion && this.liveRegion.parentNode) {
            this.liveRegion.parentNode.removeChild(this.liveRegion);
        }

        // Clear stored widths
        this.originalWidths.clear();
    }
}

export default Tab;